// @ts-nocheck
import { DataTableCell } from "@insite/mobius/index";
import LoadingOverlay from "@insite/mobius/LoadingOverlay";

import useLocationFilterSearch from "@insite/client-framework/Common/Hooks/useLocationFilterSearch";
import translate from "@insite/client-framework/Translate";
import DynamicDropdown from "@insite/mobius/DynamicDropdown";
import useLocalizeCurrency from "@store/Framework/Common/Hooks/useLocalizeCurrency";
import useReportApi from "@store/Framework/Common/Hooks/useReportAPI/useReportAPI";
import { ServiceHistoryItemModel, ServiceHistoryModel } from "@store/Framework/Services/Reporting/ReportingModels";
import { isServiceHistoryModel } from "@store/Framework/Services/Reporting/ReportingTypeGuards";
import { CompanyLocation } from "@store/Framework/Store/Bunzl/Pages/Reporting/LoadLocations";
import { locationsSelector } from "@store/Framework/Store/Bunzl/Pages/Reporting/ReportOrderFrequency/ReportOrderFrequencySelectors";
import useShipTo from "@store/Framework/Store/Bunzl/Pages/Reporting/StoreHooks/useShipTo";
import { CustomWidgetModule } from "@store/Framework/Types/types";
import ReportDataTable, {
    DataTableData,
    FormatDictionary,
} from "@store/Library/Components/Reporting/ApiDataTable/ReportDataTable";
import AccordionLayout from "@store/Library/Components/Reporting/Common/AccordionLayout";
import ReportContainer from "@store/Library/Components/Reporting/Common/ReportContainer";
import ReportingFilterGridItem from "@store/Library/Components/Reporting/Common/ReportingFilterGridItem";
import useLocation from "@store/Library/Components/Reporting/LocationPicker/useLocation";
import ServiceHistoryModal from "@store/Library/Components/Reporting/ServiceHistoryModal/ServiceHistoryModal";
import React, { useEffect, useMemo } from "react";
import { useSelector } from "react-redux";
import { css } from "styled-components";

const serviceHistoryDataKeys: (keyof ServiceHistoryItemModel)[] = [
    "invoiceNumber",
    "orderNumber",
    "serialNumber",
    "model",
    "orderDate",
    "invoiceAmount",
];

const serviceHistoryHeaders = ["Invoice #", "Order #", "Serial Number", "Model", "Order Date", "Invoice Amount"];

const ServiceHistoryReport: React.FC = () => {
    const { updateData } = useReportApi("serviceHistory");
    const [serviceHistoryData, setServiceHistoryData] = React.useState<ServiceHistoryModel>();
    const [selectedLocation, setSelectedLocation] = React.useState<string>();
    const [selectedShipTo, setSelectedShipTo] = React.useState<string>();
    const shipTo = useShipTo();
    const localizeCurrency = useLocalizeCurrency();
    useLocation();
    const [hasLoaded, setHasLoaded] = React.useState(false);
    const formatDictionary: FormatDictionary = {
        invoiceAmount: localizeCurrency,
    };

    const locationData = useSelector(locationsSelector);
    const updateShipTo = async (shipTo: string | undefined) => {
        if (!shipTo) {
            return;
        }
        setHasLoaded(false);
        setSelectedShipTo(shipTo);
        const serviceHistoryReturn = await updateData({
            customer: shipTo,
            reportName: "serviceHistory",
        });

        if (serviceHistoryReturn && isServiceHistoryModel(serviceHistoryReturn)) {
            setServiceHistoryData(serviceHistoryReturn);
        } else {
            setServiceHistoryData(undefined);
        }
        setHasLoaded(true);
    };

    useEffect(() => {
        if (shipTo) {
            updateShipTo(shipTo);
        }
    }, [shipTo]);
    useEffect(() => {
        const shipToLocation: CompanyLocation | undefined = locationData?.find((location: CompanyLocation) =>
            location.customers?.some(customer => customer.id === shipTo),
        );
        setSelectedLocation(shipToLocation?.name);
    }, [shipTo, locationData]);

    const locationsOptions =
        locationData?.map(location => ({ optionText: location.name, optionValue: location.name })) || [];

    const shipTosOptions =
        locationData
            ?.find(location => location.name === selectedLocation)
            ?.customers.map(customer => ({
                optionText: `${customer.name} (${customer.customerSequence})`,
                optionValue: customer.id,
            })) || [];

    const tableData = useMemo(
        () =>
            serviceHistoryData?.items.map(serviceHistoryItem => {
                const element = (
                    <DataTableCell
                        key={serviceHistoryItem.invoiceNumber}
                        css={css`
                            display: flex;
                            justify-content: center;
                        `}
                    >
                        <ServiceHistoryModal serviceHistory={serviceHistoryItem} />
                    </DataTableCell>
                );
                return { ...serviceHistoryItem, extraElement: element };
            }) || [],
        [serviceHistoryData?.items.length],
    );

    const serviceHistoryTableData: DataTableData<ServiceHistoryItemModel> = {
        title: translate("Service History Report"),
        data: tableData,
        headers: serviceHistoryHeaders,
        dataKeys: serviceHistoryDataKeys,
        dataSummaryTitle: translate("Service History"),
        uniqueKey: "invoiceNumber",
        formatDictionary,
    };

    return (
        <ReportContainer>
            <AccordionLayout>
                <ReportingFilterGridItem>
                    <DynamicDropdown
                        label={translate("Location")}
                        options={locationsOptions}
                        onSelectionChange={value => {
                            setSelectedLocation(value);
                            setSelectedShipTo(undefined);
                        }}
                        selected={selectedLocation}
                    />
                </ReportingFilterGridItem>
                <ReportingFilterGridItem>
                    <DynamicDropdown
                        options={shipTosOptions}
                        selected={selectedShipTo}
                        label={translate("Ship To")}
                        onSelectionChange={updateShipTo}
                    />
                </ReportingFilterGridItem>
            </AccordionLayout>
            <LoadingOverlay loading={!hasLoaded}>
                <ReportDataTable key={tableData.length} dataTableData={serviceHistoryTableData} />
            </LoadingOverlay>
        </ReportContainer>
    );
};
const widgetModule: CustomWidgetModule = {
    component: ServiceHistoryReport,
    definition: {
        group: "Reporting",
        displayName: "Service History Report",
    },
};

export default widgetModule;
